
import { useState, useEffect } from "react";
import { TrendingUp, DollarSign, Calendar, ArrowUp, ArrowDown } from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend
} from "recharts";
import DashboardLayout from "../../components/DashboardLayout";
import DateRangePicker from "../../components/DateRangePicker";
import { reportsApi } from "@/lib/api";
import { formatCurrencyFull, localToday } from "@/lib/data";

const fmt = (n: number) => formatCurrencyFull(n);

function KpiCard({ label, value, sub, icon: Icon, color, bg, trend }: any) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-stone-100`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`w-9 h-9 rounded-lg ${color} bg-white/80 flex items-center justify-center`}>
          <Icon className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <span className={`flex items-center gap-0.5 text-xs font-medium ${trend >= 0 ? "text-emerald-600" : "text-red-500"}`}>
            {trend >= 0 ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
            {Math.abs(trend).toFixed(1)}%
          </span>
        )}
      </div>
      <p className="text-xs font-medium text-stone-500 mb-1">{label}</p>
      <p className="text-2xl font-bold text-stone-800">{value}</p>
      {sub && <p className="text-xs text-stone-500 mt-1">{sub}</p>}
    </div>
  );
}

const MONTH_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4"];

export default function RevenueTrendsPage() {
  const today = localToday();
  const firstOfYear = today.slice(0, 4) + "-01-01";

  const [fromDate, setFromDate] = useState(firstOfYear);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await reportsApi.revenueTrends({ from_date: fromDate, to_date: toDate });
      setData(res.data?.data);
    } catch { /* empty */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [fromDate, toDate]);

  const monthly: any[] = data?.monthly ?? [];
  const summary = data?.summary ?? {};

  const totalRevenue = summary.total_revenue ?? 0;
  const avgMonthly   = summary.avg_monthly_revenue ?? 0;
  const bestMonth    = summary.best_month ?? null;
  const totalEvents  = monthly.reduce((s: number, m: any) => s + Number(m.event_count ?? 0), 0);

  const chartData = monthly.map((m: any) => {
    const rev = Number(m.revenue ?? 0);
    const ev  = Number(m.event_count ?? 0);
    return {
      month: m.month_name ?? m.month,
      revenue: rev,
      events: ev,
      avg_per_event: ev > 0 ? rev / ev : 0,
    };
  });

  const peakRevenue = Math.max(...chartData.map(d => d.revenue), 1);

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-orange-600" />
            <h1 className="text-lg font-bold text-stone-800">Revenue Trends</h1>
          </div>
          <DateRangePicker
            fromDate={fromDate} toDate={toDate}
            onFromChange={setFromDate} onToChange={setToDate}
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-stone-400 text-sm">Loading…</div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard label="Total Revenue" value={fmt(totalRevenue)} sub={`${totalEvents} events`} icon={DollarSign} color="text-emerald-600" bg="bg-emerald-50" />
              <KpiCard label="Avg Monthly Revenue" value={fmt(avgMonthly)} sub={`${monthly.length} months`} icon={TrendingUp} color="text-orange-600" bg="bg-orange-50" />
              <KpiCard label="Best Month" value={bestMonth?.month_name ?? "—"} sub={bestMonth ? fmt(bestMonth.revenue) : ""} icon={Calendar} color="text-blue-600" bg="bg-blue-50" />
              <KpiCard label="Avg per Event" value={totalEvents > 0 ? fmt(totalRevenue / totalEvents) : "—"} sub={`${totalEvents} events total`} icon={TrendingUp} color="text-violet-600" bg="bg-violet-50" />
            </div>

            {/* Area Chart */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <h2 className="text-sm font-semibold text-stone-700 mb-4">Monthly Revenue</h2>
              {chartData.length === 0 ? (
                <div className="flex items-center justify-center h-52 text-stone-400 text-sm">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v: any, name: any) => [
                      name === "revenue" ? fmt(v) : v,
                      name === "revenue" ? "Revenue" : "Events"
                    ]} />
                    <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" strokeWidth={2} fill="url(#revGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Monthly Bar Chart */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <h2 className="text-sm font-semibold text-stone-700 mb-4">Revenue vs Events per Month</h2>
              {chartData.length === 0 ? (
                <div className="flex items-center justify-center h-52 text-stone-400 text-sm">No data</div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} barCategoryGap="30%">
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f4" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="rev" tick={{ fontSize: 11 }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                    <YAxis yAxisId="ev" orientation="right" tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v: any, name: any) => [
                      name === "revenue" ? fmt(v) : v,
                      name === "revenue" ? "Revenue" : "Events"
                    ]} />
                    <Legend formatter={(v) => v === "revenue" ? "Revenue" : "Events"} />
                    <Bar yAxisId="rev" dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar yAxisId="ev" dataKey="events" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Monthly Table */}
            <div className="bg-white rounded-xl border border-stone-200 p-4">
              <h2 className="text-sm font-semibold text-stone-700 mb-3">Monthly Breakdown</h2>
              {chartData.length === 0 ? (
                <p className="text-sm text-stone-400">No data for this period</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-stone-100">
                        <th className="text-left py-2 text-xs font-semibold text-stone-500">Month</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Revenue</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Events</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Avg/Event</th>
                        <th className="text-right py-2 text-xs font-semibold text-stone-500">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-50">
                      {chartData.map((row, i) => {
                        const share = totalRevenue > 0 ? (row.revenue / totalRevenue) * 100 : 0;
                        const isBest = row.revenue === peakRevenue && row.revenue > 0;
                        return (
                          <tr key={i} className={isBest ? "bg-emerald-50" : ""}>
                            <td className="py-2.5 text-stone-700 font-medium">
                              {row.month}
                              {isBest && <span className="ml-2 text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Best</span>}
                            </td>
                            <td className="py-2.5 text-right font-semibold text-stone-800">{fmt(row.revenue)}</td>
                            <td className="py-2.5 text-right text-stone-600">{row.events}</td>
                            <td className="py-2.5 text-right text-stone-500">{row.events > 0 ? fmt(row.revenue / row.events) : "—"}</td>
                            <td className="py-2.5 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                  <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${share}%` }} />
                                </div>
                                <span className="text-xs text-stone-400 w-8 text-right">{share.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-stone-200 bg-stone-50">
                        <td className="py-2.5 font-bold text-stone-800">Total</td>
                        <td className="py-2.5 text-right font-bold text-emerald-700">{fmt(totalRevenue)}</td>
                        <td className="py-2.5 text-right font-bold text-stone-700">{totalEvents}</td>
                        <td className="py-2.5 text-right text-stone-500">{totalEvents > 0 ? fmt(totalRevenue / totalEvents) : "—"}</td>
                        <td className="py-2.5 text-right text-xs text-stone-400">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
